import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Checkbox } from '@fiverr-private/forms';
import { Button } from '@fiverr-private/button';
import { Container, Section } from '@fiverr-private/layout_components';
import { Heading, Text } from '@fiverr-private/typography';
import { raiseToast } from '@fiverr-private/toast';
import { PrivacyLinkCard } from '../PrivacyLinkCard';
import { CPRA_COPIES } from '../../../lib/copies';
import { CPRA_PAGE_URL } from '../../../lib/constants';
import { CpraSettings } from '../../../lib/cpraService';
import { trackCPRAChoiceChange, trackPageViewEvent } from '../../../lib/tracking';

interface CPRAOptOutPageProps {
  isCPRAOptOut: boolean;
}

const raiseSuccessToast = () =>
  raiseToast({
    message: CPRA_COPIES.SUCCESS_TOAST_MESSAGE,
    type: 'success',
    location: 'top',
  });

const raiseErrorToast = () =>
  raiseToast({
    message: CPRA_COPIES.ERROR_TOAST_MESSAGE,
    type: 'error',
    location: 'top',
  });

export const CPRAOptOutPage: React.FC<CPRAOptOutPageProps> = ({ isCPRAOptOut }) => {
  const [initialValue, setInitialValue] = useState(isCPRAOptOut);
  const [checkboxValue, setNewValue] = useState(isCPRAOptOut);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    trackPageViewEvent();
  }, []);

  const saveChoice = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      await axios.put<CpraSettings>(CPRA_PAGE_URL, { stopSharingData: checkboxValue });
      raiseSuccessToast();
      setInitialValue(checkboxValue);
      trackCPRAChoiceChange(checkboxValue);
    } catch (err) {
      raiseErrorToast();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Section>
      <Container
        display="flex"
        justifyContent="spaceBetween"
        direction={{
          default: 'column',
          md: 'row',
        }}
        paddingTop={{
          default: '2',
          sm: '8',
        }}
        paddingBottom="5"
        marginX="auto"
        maxWidth="1108px"
      >
        <Container
          maxWidth={{
            xl: '652px',
            lg: '626px',
            md: '515px',
          }}
          gap="8"
          display="flex"
          direction="column"
        >
          <Heading marginBottom="4" size="h_xl" as="h1">
            {CPRA_COPIES.HEADING}
          </Heading>
          <Text color="bodyPrimary">{CPRA_COPIES.FIRST_TEXT_BLOCK_CONTENT}</Text>
          <Text color="bodyPrimary">{CPRA_COPIES.SECOND_TEXT_BLOCK_CONTENT}</Text>
          <Text color="bodySecondary" size="b_xs">
            {CPRA_COPIES.DISCLAIMER_TEXT_BLOCK_CONTENT}
          </Text>
          <Container marginTop="4">
            <Checkbox disabled={isLoading} checked={checkboxValue} onChange={(e) => setNewValue(e.target.checked)}>
              <span className="text-semi-bold">{CPRA_COPIES.CHECKBOX_SAVE_LABEL}</span>
            </Checkbox>
          </Container>
          <Container display="flex" justifyContent="flexEnd" paddingY="4">
            <Button size="sm" disabled={initialValue === checkboxValue} loading={isLoading} onClick={saveChoice}>
              {CPRA_COPIES.SAVE_BUTTON}
            </Button>
          </Container>
        </Container>
        <Container
          maxWidth={{
            md: '256px',
            lg: '310px',
          }}
          display="flex"
          direction="column"
          flexShrink="0"
        >
          <PrivacyLinkCard />
        </Container>
      </Container>
    </Section>
  );
};
